import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame } from '@react-three/fiber';
import { Environment, Lightformer } from '@react-three/drei';
import { EffectComposer, ChromaticAberration } from '@react-three/postprocessing';
import gsap from 'gsap';

const Building = ({ x, z, w, h, d, color, mountProgress }) => {
  const group = useRef();

  const { geom, edgesGeom } = useMemo(() => {
    const g = new THREE.BoxGeometry(w, h, d);
    const eg = new THREE.EdgesGeometry(g);
    return { geom: g, edgesGeom: eg };
  }, [w, h, d]);

  useEffect(() => {
    return () => {
      geom.dispose();
      edgesGeom.dispose();
    };
  }, [geom, edgesGeom]);

  useFrame((state) => {
    if (!group.current) return;
    const scaleY = mountProgress.current * (1 + Math.sin(state.clock.elapsedTime * 0.45 + x * 2) * 0.02);
    group.current.scale.set(1, scaleY, 1);
    group.current.position.y = -1.5 + (h * scaleY) / 2;
  });

  return (
    <group ref={group} position={[x, 0, z]}>
      <mesh geometry={geom}>
        <meshPhysicalMaterial
          color={color}
          roughness={0.12}
          metalness={color === '#C9971B' ? 0.8 : 0.15}
          transparent
          opacity={color === '#FFFFFF' ? 0.72 : 0.82}
          transmission={color === '#FFFFFF' ? 0.75 : 0.15}
          thickness={0.8}
          envMapIntensity={1.5}
        />
      </mesh>
      <lineSegments geometry={edgesGeom}>
        <lineBasicMaterial color={color === '#FFFFFF' ? '#1E3A7B' : color} transparent opacity={0.35} />
      </lineSegments>
    </group>
  );
};

const Traffic = () => {
  const cars = useMemo(() => {
    return [
      { axis: 'x', route: -1.8, speed: 1.0, offset: -5, color: '#C9971B' },
      { axis: 'x', route: 1.8, speed: -0.8, offset: 4, color: '#1E3A7B' },
      { axis: 'z', route: -3.6, speed: 1.2, offset: -3, color: '#1E7A34' },
      { axis: 'z', route: 3.6, speed: -0.9, offset: 2, color: '#C9971B' },
    ];
  }, []);

  const meshRefs = useRef([]);

  useFrame((state, delta) => {
    cars.forEach((car, i) => {
      const mesh = meshRefs.current[i];
      if (!mesh) return;
      if (car.axis === 'x') {
        mesh.position.x += car.speed * delta;
        if (mesh.position.x > 7) mesh.position.x = -7;
        if (mesh.position.x < -7) mesh.position.x = 7;
      } else {
        mesh.position.z += car.speed * delta;
        if (mesh.position.z > 7) mesh.position.z = -7;
        if (mesh.position.z < -7) mesh.position.z = 7;
      }
    });
  });

  return (
    <>
      {cars.map((car, i) => (
        <mesh
          key={i}
          ref={(el) => (meshRefs.current[i] = el)}
          position={car.axis === 'x' ? [car.offset, -1.47, car.route] : [car.route, -1.47, car.offset]}
        >
          <sphereGeometry args={[0.05, 8, 8]} />
          <meshBasicMaterial color={car.color} />
        </mesh>
      ))}
    </>
  );
};

const Particles = ({ count }) => {
  const ref = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = Math.random() * 8;
      const theta = Math.random() * Math.PI * 2;
      arr[i * 3] = Math.cos(theta) * r;
      arr[i * 3 + 1] = -1.5 + Math.random() * 6;
      arr[i * 3 + 2] = Math.sin(theta) * r;
    }
    return arr;
  }, [count]);

  useFrame((state, delta) => {
    if (ref.current) {
      ref.current.rotation.y += delta * 0.04;
      const pos = ref.current.geometry.attributes.position;
      const arr = pos.array;
      for (let i = 1; i < arr.length; i += 3) {
        arr[i] += delta * 0.16;
        if (arr[i] > 4.5) {
          arr[i] = -1.5;
        }
      }
      pos.needsUpdate = true;
    }
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        color="#C9971B"
        size={0.035}
        sizeAttenuation
        transparent
        opacity={0.6}
        depthWrite={false}
      />
    </points>
  );
};

const Rig = ({ progressRef, parallax, isMobile }) => {
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!parallax) return;
    const onMove = (e) => {
      mouse.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.current.y = -(e.clientY / window.innerHeight) * 2 + 1;
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [parallax]);

  useFrame((state, delta) => {
    const p = progressRef.current;
    
    // Circular camera orbit path driven by scroll + idle rotation
    const r = (isMobile ? 8.0 : 6.2) - p * 1.6;
    const theta = state.clock.elapsedTime * 0.045 + p * Math.PI * 0.42;
    const height = (isMobile ? 2.6 : 1.95) - p * 1.35 + mouse.current.y * 0.45;

    const cam = state.camera;
    const targetX = Math.sin(theta) * r + mouse.current.x * 0.55;
    const targetZ = Math.cos(theta) * r;

    cam.position.x = THREE.MathUtils.damp(cam.position.x, targetX, 2.5, delta);
    cam.position.y = THREE.MathUtils.damp(cam.position.y, height, 2.5, delta);
    cam.position.z = THREE.MathUtils.damp(cam.position.z, targetZ, 2.5, delta);
    
    cam.lookAt(0, -0.4, 0);
  });
  return null;
};

export const HeroScene = ({ progressRef, isMobile, reduced, dpr, particles }) => {
  const mountProgress = useRef(0);

  useEffect(() => {
    const t = gsap.to(mountProgress, {
      current: 1,
      duration: 2.2,
      ease: 'power3.out',
      delay: 0.8
    });
    return () => t.kill();
  }, []);

  const buildings = useMemo(() => {
    const list = [];
    const size = 7;
    const spacing = 1.75;
    const half = (size - 1) / 2;

    for (let x = 0; x < size; x++) {
      for (let z = 0; z < size; z++) {
        const dx = x - half;
        const dz = z - half;

        // Landmark central skyscraper
        if (dx === 0 && dz === 0) {
          list.push({
            id: 'center',
            x: 0,
            z: 0,
            w: 0.85,
            h: 4.6,
            d: 0.85,
            color: '#C9971B'
          });
          continue;
        }

        // Empty cross-road paths passing through center
        if (dx === 0 || dz === 0) {
          continue;
        }

        const dist = Math.hypot(dx, dz);
        const maxH = Math.max(1.0, 4.0 - dist * 0.8);
        const h = maxH * (0.85 + Math.random() * 0.3);

        const w = 0.6 + Math.random() * 0.45;
        const d = 0.6 + Math.random() * 0.45;

        // Color mix: glass, gold, navy blue, and green highlights
        let color = '#FFFFFF';
        const rand = Math.random();
        if (rand > 0.8) color = '#C9971B';
        else if (rand > 0.6) color = '#1E3A7B';
        else if (rand > 0.5) color = '#1E7A34';

        list.push({
          id: `${x}-${z}`,
          x: dx * spacing + (Math.random() - 0.5) * 0.15,
          z: dz * spacing + (Math.random() - 0.5) * 0.15,
          w,
          h,
          d,
          color
        });
      }
    }
    return list;
  }, []);

  return (
    <Canvas
      dpr={dpr}
      camera={{ position: isMobile ? [0, 2.6, 8.0] : [0, 2, 6.2], fov: isMobile ? 50 : 45 }}
      gl={{ antialias: true, alpha: false, powerPreference: 'high-performance' }}
    >
      <color attach="background" args={['#FFFFFF']} />
      <fog attach="fog" args={['#FFFFFF', 8, 26]} />

      <ambientLight intensity={0.9} />
      <pointLight position={[6, 4, 5]} color="#C9971B" intensity={40} distance={20} />
      <pointLight position={[-6, 2, 4]} color="#1E3A7B" intensity={50} distance={20} />
      <directionalLight position={[0, 8, 2]} intensity={1.5} color="#FFFFFF" />

      {buildings.map((b) => (
        <Building
          key={b.id}
          x={b.x}
          z={b.z}
          w={b.w}
          h={b.h}
          d={b.d}
          color={b.color}
          mountProgress={mountProgress}
        />
      ))}

      <Traffic />
      <Particles count={particles} />
      <Rig progressRef={progressRef} parallax={!isMobile && !reduced} isMobile={isMobile} />

      <Environment resolution={64}>
        <Lightformer intensity={2} position={[0, 5, 5]} scale={[10, 5, 1]} color="#ffffff" />
        <Lightformer intensity={4} position={[5, 1, -2]} scale={[3, 8, 1]} color="#C9971B" />
        <Lightformer intensity={3} position={[-5, 0, -2]} scale={[3, 8, 1]} color="#1E3A7B" />
      </Environment>

      {!isMobile && !reduced && (
        <EffectComposer multisampling={0}>
          <ChromaticAberration offset={[0.0008, 0.0005]} />
        </EffectComposer>
      )}
    </Canvas>
  );
};
